import { useState, useEffect } from "react";
import { useProviderRegister } from "contexts/register/ProviderContext";

import { IProvider } from "interfaces/Provider";
import { IExpressValidatorError } from "interfaces/ExpressError";

import {
  handleProviderUpdateSubmit,
  handleProviderCreateSubmit,
} from "services/handles/ProviderHandles";

import { StepModalTemplate } from "templates/StepModalTemplate";

import { transformValidatorError } from "utils/transformValidatorError";

import { Tab } from "../../../TabHeader";

import * as S from "./styles";
import { ProviderUserForm } from "../../../Forms/Provider/ProviderUserForm";
import { ServiceForm } from "components/Forms/Service/ServiceForm";
import { useServiceRegister } from "contexts/register/ServiceContext";

type Props = {
  opened: boolean;
  setOpened: (value: boolean) => void;
  providerData?: IProvider;
};

const tabs: Tab[] = [{ index: 0, text: "Dados da empresa" }, { index: 1, text: "Serviços Prestados" }];

function StepRegisterProvider({ opened, setOpened, providerData }: Props) {
  const {
    provider,
    setErrors,
    searching,
    storeProvider,
    resetProvider,
    user,
    errors,
  } = useProviderRegister();
  const {services} = useServiceRegister()
  const [tab, setTab] = useState<Tab>(tabs[0]);
  const [invalidTabs, setInvalidTabs] = useState<Tab[]>([]);

  const [sending, setSending] = useState<boolean>(false);

  useEffect(() => {
    if (providerData) {
      setInvalidTabs([]);
      setErrors({});
      storeProvider(providerData);

      return;
    }
    resetProvider();
  }, [providerData]); // eslint-disable-line react-hooks/exhaustive-deps

  function treatDuplicatedValidator(data: {
    [key: string]: { message: string; value: any };
  }) {
    let response: any = {};
    for (const key in data) {
      if (key.includes("cnpj") || key.includes("cpf") || key.includes("rg"))
        setTabAsInvalid(tabs[0]);
      response[key] = data[key].message;
    }
    setErrors((err) => {
      return { ...err, ...response };
    });
  }
  async function handleSubmit() {
    setSending(true);
    // check if its update or create
    if (providerData) {
      // checar se é pessoa física ou jurídica (para mandar ou cpf ou cnpj)
      if (user.type === 1) {
        delete user.cnpj;
        return await handleProviderUpdateSubmit({
          userId: providerData.user.id,
          cpf: user.cpf,
          email: user.email,
          name: provider.name,
          rg: user.rg,
          providerId: providerData.id,
          managerName: provider.managerName,
          tradingName: provider.tradingName,
          services
          })
          .then((res) => {
            if (res.status === true) {
              localStorage.setItem(
                "message",
                "Os dados do fornecedor foram salvos corretamente."
              );
              window.location.reload();
            }
            if (res.data.duplicated) {
              for (const key in res.data.err) {
                treatDuplicatedValidator(res.data.err[key]);
              }
            } else {
              treatValidatorErrors(res.data.error);
            }
          })
          .catch((error) => {
            treatValidatorErrors(error.data);
          })
          .finally(() => {
            setSending(false);
          });
      } else {
        delete user.cpf;
        delete user.rg;
        return await handleProviderUpdateSubmit({
          userId: providerData.user.id,
          cnpj: user.cnpj,
          email: user.email,
          name: provider.name,
          rg: user.rg,
          providerId: providerData.id,
          managerName: provider.managerName,
          tradingName: provider.tradingName,
          role: user.type ? user.type : 2,
          services
        })
          .then((res) => {
            if (res.status === true) {
              localStorage.setItem(
                "message",
                "Os dados do fornecedor foram salvos corretamente."
              );
              window.location.reload();
            }
            if (res.data.duplicated) {
              for (const key in res.data.err) {
                treatDuplicatedValidator(res.data.err[key]);
              }
            } else {
              treatValidatorErrors(res.data.error);
            }
          })
          .catch((error) => {
            treatValidatorErrors(error.response.data.error);
          })
          .finally(() => {
            setSending(false);
          });
      }
    }

    if (provider && user) {
      return await handleProviderCreateSubmit({
        name: provider.name,
        password: user.password,
        rg: user.rg,
        cpf: user.cpf,
        cnpj: user.cnpj,
        managerName: provider.managerName,
        tradingName: provider.tradingName,
        email: user.email,
        role: user.type ? user.type : 2,
        services
      })
        .then((res) => {
          if (res.status === true) {
            localStorage.setItem(
              "message",
              "Os dados do fornecedor foram cadastrados corretamente."
            );
            window.location.reload();
          }
          if (res.data.duplicated) {
            treatDuplicatedValidator(res.data.error);
          } else {
            treatValidatorErrors(res.data.error);
          }
        })
        .catch((error) => {
          treatValidatorErrors(error.response.data.error);
        })
        .finally(() => {
          setSending(false);
        });
    }

    setSending(false);
  }
  function setTabAsInvalid(tab: Tab) {
    setInvalidTabs((list) => [...list, tab]);
  }

  function treatValidatorErrors(validatorErrors: IExpressValidatorError) {
    setInvalidTabs([]);

    if (validatorErrors === undefined) return;

    const { errors, invalidFormTypes } =
      transformValidatorError(validatorErrors);
    if (invalidFormTypes.length) {
      setTabAsInvalid(tabs[0]);
    }

    setErrors(errors);
  }

  useEffect(() => {
    if (!Object.keys(errors).length) {
      return setInvalidTabs([]);
    }
    setTabAsInvalid(tabs[0]);
  }, [errors]);

  function onTabClick(_tab: Tab) {
    setTab(_tab);
  }
  return (
    <StepModalTemplate
      deactivatedTabs={[]}
      disableButton={Object.keys(errors).length > 0}
      title={`${providerData ? "Editar" : "Cadastrar"} fornecedor`}
      buttonText={`${providerData ? "Salvar" : "Efetuar cadastro"}`}
      opened={opened}
      setOpened={setOpened}
      tabs={tabs}
      invalidTabs={invalidTabs}
      selectedTab={tab}
      sending={sending}
      setSelectedTab={onTabClick}
      handleSubmit={handleSubmit}
    >
      <S.Container>
        {searching && (
          <S.LoadingWrapper>
            <span>Buscando dados...</span>
          </S.LoadingWrapper>
        )}

        {!searching && (
          <div>
            {tab.index === 0 && (
              <ProviderUserForm isEditing={providerData !== undefined} />
            )}
            {tab.index === 1 && (
              <ServiceForm  />
            )}
          </div>
        )}
      </S.Container>
    </StepModalTemplate>
  );
}

export { StepRegisterProvider };
